import { http } from "../../utils/config";

/**
 * Khách hàng. Hàm async thuần — đã bỏ `createAsyncThunk` + slice `customer`.
 *
 * Ba thay đổi đáng chú ý so với bản cũ:
 *
 * 1. Query dựng bằng `params` của axios thay vì nối chuỗi tay
 *    (`apiUrl += "&search=" + value`). Bản cũ KHÔNG encode nên tên khách chứa
 *    `&` hoặc `#` là cắt mất phần sau, và khoảng trắng thành query lỗi.
 * 2. Nhận `signal` để `useAsync` hủy được request cũ — hết cảnh phản hồi của từ
 *    khóa trước về sau và ghi đè kết quả từ khóa hiện tại.
 * 3. Lỗi để nguyên. Bản cũ `catch { throw new Error("Có lỗi xảy ra trong quá trình
 *    kết nối") }` nên trùng số điện thoại (422) hay hết quyền (403) đều hiện cùng
 *    một câu vô nghĩa.
 */

const BASE = "/mcoupon/api/v1/customer";

/** Bỏ field rỗng: BE coi thiếu tham số là "không lọc theo tiêu chí này". */
function compact(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Bộ lọc theo ID: `0` trong CMS này nghĩa là "tất cả", không phải id 0.
 * Bản cũ nối chuỗi bằng `if (branchId)` nên `0` tự rơi — `compact()` thì không,
 * nên phải chặn ở đây kẻo lọc theo chi nhánh id=0 và ra danh sách rỗng.
 * LƯU Ý: cố tình KHÔNG áp cho `isLocked` — ở đó `0` là giá trị thật (chưa khóa).
 */
const idFilter = (v) => (v === 0 || v === "0" ? undefined : v);

export const getListCustomer = (
  {
    currentPage = 1,
    pageSize = 10,
    value,
    branchId,
    fromDate,
    toDate,
    customerType,
    isLocked,
    status,
    trashed,
  } = {},
  signal
) =>
  http
    .get(`${BASE}/getList`, {
      signal,
      params: compact({
        has_paginate: 1,
        size: pageSize,
        page: currentPage,
        search: value,
        branch_first_id: idFilter(branchId),
        from_date: fromDate,
        to_date: toDate,
        customer_type: customerType,
        // `status` (pending/active/locked/rejected) là trục mới, thay `is_locked`.
        // Chỉ gửi `is_locked` khi không có `status` — giữ tương thích màn cũ.
        status,
        is_locked: status ? undefined : isLocked,
        // Màn Thùng rác: chỉ khách đã xóa mềm. `compact` bỏ giá trị falsy nên
        // danh sách thường không gửi tham số này.
        trashed: trashed ? 1 : undefined,
      }),
    })
    .then((res) => res.data);

/** Toàn bộ khách, không phân trang. Dùng cho dropdown — cân nhắc `searchCustomers` nếu dữ liệu lớn. */
export const getListCustomerAll = (signal) =>
  http.get(`${BASE}/getList`, { signal }).then((res) => res.data);

export const getDetailCustomer = (id, signal) =>
  http.get(`${BASE}/getId/${id}`, { signal }).then((res) => res.data);

/** Tìm khách cho picker gửi SMS — server-side search, giới hạn 50 (không tải toàn bộ). */
export const searchCustomersForSms = (search, signal) =>
  http
    .get(`${BASE}/getList`, {
      signal,
      params: compact({ has_paginate: 1, size: 50, search }),
    })
    .then((res) => res.data);

export const createCustomer = (data) =>
  http.post(`${BASE}/store`, data).then((res) => res.data);

export const editCustomer = (data) =>
  http.post(`${BASE}/update/${data.id}`, data).then((res) => res.data);

export const deleteCustomer = (id) =>
  http.delete(`${BASE}/delete/${id}`).then((res) => res.data);

export const deleteCustomers = (data) =>
  http.post(`${BASE}/mass-delete`, data).then((res) => res.data);

export const checkPhoneCustomer = (data) =>
  http.post(`${BASE}/check-phone`, data).then((res) => res.data);

export const checkPhoneCustomerAuth = (data) =>
  http.post(`${BASE}/check-phone-auth`, data).then((res) => res.data);

/* ------------------------------ Tặng điểm tay ------------------------------ */

export const givePointCustomer = (data) =>
  http
    .post("/api/v1/serviceconnect/give-points-to-customers", data)
    .then((res) => res.data);

export const getListGivePointCustomer = (
  { currentPage = 1, pageSize = 10 } = {},
  signal
) =>
  http
    .get("/api/v1/serviceconnect/give-points-to-customers", {
      signal,
      params: { has_paginate: 1, size: pageSize, page: currentPage },
    })
    .then((res) => res.data);

export const importCustomer = (data) =>
  http
    .post("/api/v1/serviceconnect/import/customers", data)
    .then((res) => res.data);
